import React from 'react';
import { ManualCreationFormProps } from './types';
import ModifierField from './ModifierField';
import UniqueIdolForm from './UniqueIdolForm';

const ManualCreationForm: React.FC<ManualCreationFormProps> = ({
  newIdolName,
  setNewIdolName,
  newIdolSize,
  setNewIdolSize,
  prefixes,
  setPrefixes,
  suffixes,
  setSuffixes,
  availablePrefixes,
  availableSuffixes,
  prefixContentTypes,
  setSelectedPrefixContentType,
  suffixContentTypes,
  setSelectedSuffixContentType,
  handleSubmit,
  onClose,
  // Unique idol props
  selectedUniqueIdol,
  setSelectedUniqueIdol,
  idolModifiers,
  setIdolModifiers,
  selectedVariableModifiers,
  setSelectedVariableModifiers,
  updateModifierValue,
  replaceVariableModifier,
  isVariableModifierSelected,
  isUniqueFormValid,
  isEditing = false
}) => {
  // Determine if form is valid
  const isFormValid = () => {
    if (newIdolSize === 'UNIQUE') {
      // For unique idols, use the isUniqueFormValid function
      return !!selectedUniqueIdol && isUniqueFormValid();
    } else {
      // For regular idols, check if at least one prefix or suffix has text
      // If a modifier has text but no value, it's still valid if there's no range
      return prefixes.some(p => {
        if (!p.text) return false;
        
        // Find the mod in available prefixes
        const mod = availablePrefixes.find(m => m.description === p.text);
        // If the mod has a range, it needs a value; otherwise, just text is enough
        return mod && (!mod.range || p.value);
      }) || 
      suffixes.some(s => {
        if (!s.text) return false;
        
        // Find the mod in available suffixes
        const mod = availableSuffixes.find(m => m.description === s.text);
        // If the mod has a range, it needs a value; otherwise, just text is enough
        return mod && (!mod.range || s.value);
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4">
        <label className="block text-sm mb-1">Name</label>
        <input 
          type="text" 
          value={newIdolName} 
          onChange={(e) => setNewIdolName(e.target.value)} 
          className="w-full p-2 rounded bg-gray-700 text-white"
          placeholder={newIdolSize === 'UNIQUE' ? 'Pick unique item below' : 'Enter idol name...'}
          disabled={newIdolSize === 'UNIQUE'} // Disable when UNIQUE is selected
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-sm mb-1">Size</label>
        <select 
          value={newIdolSize} 
          onChange={(e) => setNewIdolSize(e.target.value)} 
          className={`w-full p-2 rounded ${
            isEditing 
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
              : 'bg-gray-700 text-white'
          }`}
          disabled={isEditing} // Disable the dropdown if we're editing
        >
          <option value="MINOR">Minor Idol (1x1)</option>
          <option value="TOTEMIC">Totemic Idol (1x3)</option>
          <option value="CONQUEROR">Conqueror Idol (2x2)</option>
          <option value="KAMASAN">Kamasan Idol (1x2)</option>
          <option value="NOBLE">Noble Idol (2x1)</option>
          <option value="BURIAL">Burial Idol (3x1)</option>
          <option value="UNIQUE">Unique Idol (1x1)</option>
        </select>
        {isEditing && (
          <div className="text-xs text-gray-400 mt-1">
            Size cannot be changed when editing an idol
          </div>
        )}
      </div>
      
      {newIdolSize === 'UNIQUE' ? (
        // Use the refactored UniqueIdolForm component for unique idols
        <UniqueIdolForm 
          selectedUniqueIdol={selectedUniqueIdol}
          setSelectedUniqueIdol={setSelectedUniqueIdol}
          idolModifiers={idolModifiers}
          setIdolModifiers={setIdolModifiers}
          selectedVariableModifiers={selectedVariableModifiers}
          setSelectedVariableModifiers={setSelectedVariableModifiers}
          setNewIdolName={setNewIdolName}
          updateModifierValue={updateModifierValue}
          replaceVariableModifier={replaceVariableModifier}
          isVariableModifierSelected={isVariableModifierSelected}
          isUniqueFormValid={isUniqueFormValid}
          isEditing={isEditing}
        />
      ) : (
        // Regular idol form with modifier fields
        <>
          {/* Prefixes Section */}
          <ModifierField 
            modifiers={prefixes}
            setModifiers={setPrefixes}
            availableMods={availablePrefixes}
            label="Prefixes"
            contentTypes={prefixContentTypes}
            setContentType={setSelectedPrefixContentType}
            maxCount={2}
            isEditing={isEditing}
          />
          
          {/* Suffixes Section */}
          <ModifierField 
            modifiers={suffixes}
            setModifiers={setSuffixes}
            availableMods={availableSuffixes}
            label="Suffixes"
            contentTypes={suffixContentTypes}
            setContentType={setSelectedSuffixContentType}
            maxCount={2}
            isEditing={isEditing}
          />
          
          {/* Validation message */}
          {!isFormValid() && (
            <div className="mb-4 text-red-400 text-sm">
              At least one prefix or suffix is required
            </div>
          )}
        </>
      )}
      
      {/* Form buttons */}
      <div className="flex justify-end">
        <button 
          type="button" 
          className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded text-white mr-2"
          onClick={onClose}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className={`px-4 py-2 rounded text-white ${
            isFormValid()
              ? 'bg-blue-800 hover:bg-blue-700'
              : 'bg-gray-600 cursor-not-allowed'
          }`}
          disabled={!isFormValid()}
        >
          {isEditing ? 'Save Idol' : 'Create Idol'}
        </button>
      </div>
    </form>
  );
};

export default ManualCreationForm;